// Where a visit or a sign-up came from (R6.10 AC-2): an AI assistant by name, Search, Social,
// Referral (with the site), Direct, or, for a sign-up whose first landing we never saw, Not captured.
//
// Pure and source-neutral, importable from client and server: no env, no network. Classification
// runs at read time over the stored first touch, so fixing a host list below re-classifies history.
// Slice 2 reuses `classifyTrafficSource` for GA4 session sources, so a GA4 row and a sign-up
// referrer are classified by the same code.
//
// Nothing is inferred (PRD-02): Direct needs a recorded landing with no referrer, and a sign-up with
// no landing, no referrer, no utm_source and no gclid is Not captured, never Direct.

import type { Attribution } from '@/lib/auth/attribution'

type TrafficChannel = 'ai' | 'search' | 'social' | 'referral' | 'direct'
export type AssistantId = 'chatgpt' | 'perplexity' | 'gemini' | 'claude' | 'copilot' | 'other_ai'

interface TrafficSource {
  channel: TrafficChannel
  /** Set when `channel` is `'ai'`. */
  assistant: AssistantId | null
  /** The site behind a Search, Social or Referral source; null for AI (the assistant is the name) and Direct. */
  site: string | null
}

/** A sign-up's source: a traffic channel, or `not_captured` when no first landing was ever seen. */
type SignupSourceKey = TrafficChannel | 'not_captured'

export interface SignupSource {
  source: SignupSourceKey
  assistant: AssistantId | null
  site: string | null
}

interface AiAssistant {
  id: AssistantId
  label: string
  hosts: readonly string[]
  /** Bare names a `utm_source` may carry besides the hosts, matched case-insensitively. */
  utmSources: readonly string[]
}

/** Sites counted as "Other AI": slice 3's popover reads the same list. */
export const OTHER_AI_HOSTS = [
  'deepseek.com',
  'meta.ai',
  'grok.com',
  'you.com',
  'phind.com',
  'chat.mistral.ai',
  'poe.com',
] as const

/** In AC-2's order. */
const AI_ASSISTANTS: readonly AiAssistant[] = [
  {
    id: 'chatgpt',
    label: 'ChatGPT',
    hosts: ['chatgpt.com', 'chat.openai.com'],
    utmSources: ['chatgpt', 'openai'],
  },
  {
    id: 'perplexity',
    label: 'Perplexity',
    hosts: ['perplexity.ai'],
    utmSources: ['perplexity'],
  },
  {
    id: 'gemini',
    label: 'Gemini',
    hosts: ['gemini.google.com', 'bard.google.com'],
    utmSources: ['gemini', 'bard'],
  },
  { id: 'claude', label: 'Claude', hosts: ['claude.ai'], utmSources: ['claude'] },
  {
    id: 'copilot',
    label: 'Copilot',
    hosts: ['copilot.microsoft.com', 'copilot.cloud.microsoft'],
    utmSources: ['copilot'],
  },
  {
    id: 'other_ai',
    label: 'Other AI assistant',
    hosts: OTHER_AI_HOSTS,
    utmSources: ['deepseek', 'grok', 'phind', 'poe', 'mistral'],
  },
]

/** The assistants in AC-2's order: the AI group of `/admin/signups` lists them in this order. */
export const AI_ASSISTANT_IDS: readonly AssistantId[] = AI_ASSISTANTS.map(
  (assistant) => assistant.id
)

const SEARCH_HOST_PATTERNS: readonly RegExp[] = [
  /^google\.(?:com?\.[a-z]{2}|[a-z]{2,3})$/,
  /^yandex\.(?:com?\.[a-z]{2}|[a-z]{2,3})$/,
]
const SEARCH_HOSTS = [
  'bing.com',
  'duckduckgo.com',
  'yahoo.com',
  'ecosia.org',
  'search.brave.com',
  'baidu.com',
  'startpage.com',
  'qwant.com',
] as const
const SEARCH_UTM_NAMES = [
  'google',
  'bing',
  'duckduckgo',
  'yahoo',
  'yandex',
  'ecosia',
  'brave',
  'baidu',
  'startpage',
  'qwant',
] as const

const SOCIAL_HOSTS = [
  'reddit.com',
  'linkedin.com',
  'lnkd.in',
  'x.com',
  'twitter.com',
  't.co',
  'facebook.com',
  'instagram.com',
  'youtube.com',
  'youtu.be',
  'news.ycombinator.com',
  'threads.net',
  'bsky.app',
] as const
const SOCIAL_UTM_NAMES = [
  'reddit',
  'linkedin',
  'x',
  'twitter',
  'facebook',
  'instagram',
  'youtube',
  'hackernews',
  'threads',
  'bluesky',
] as const

/** Lower-cased, trimmed, one trailing dot (FQDN form) and `www.` stripped: the form every list above is written in. */
export function normaliseHost(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/^www\./, '')
}

/** A host matches a listed domain when it is that domain or any sub-host of it. */
function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`)
}

function matchesAny(host: string, domains: readonly string[]): boolean {
  return domains.some((domain) => hostMatches(host, domain))
}

function isSearchHost(host: string): boolean {
  return (
    SEARCH_HOST_PATTERNS.some((pattern) => pattern.test(host)) || matchesAny(host, SEARCH_HOSTS)
  )
}

function assistantForHost(host: string): AssistantId | null {
  for (const assistant of AI_ASSISTANTS) {
    if (matchesAny(host, assistant.hosts)) return assistant.id
  }
  return null
}

function assistantForUtmSource(utmSource: string): AssistantId | null {
  const value = normaliseHost(utmSource)
  for (const assistant of AI_ASSISTANTS) {
    if (assistant.utmSources.includes(value) || matchesAny(value, assistant.hosts)) {
      return assistant.id
    }
  }
  return null
}

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function aiSource(assistant: AssistantId): TrafficSource {
  return { channel: 'ai', assistant, site: null }
}

/**
 * One visit's source. An AI assistant if the referrer host or `utm_source` names one (the referrer
 * is checked first); otherwise by the referrer host; with no referrer, a `gclid` is Search and a
 * `utm_source` naming a search or social site is that channel, any other `utm_source` a Referral
 * with the value as its site; nothing at all is Direct. A malformed host or value is a Referral
 * with the raw value as its site, never a throw.
 */
export function classifyTrafficSource(input: {
  referrerHost?: string | null
  utmSource?: string | null
  gclid?: string | null
}): TrafficSource {
  const referrerRaw = nonEmpty(input.referrerHost)
  const utmRaw = nonEmpty(input.utmSource)
  const referrerHost = referrerRaw === null ? null : normaliseHost(referrerRaw)

  if (referrerHost !== null) {
    const assistant = assistantForHost(referrerHost)
    if (assistant !== null) return aiSource(assistant)
  }
  if (utmRaw !== null) {
    const assistant = assistantForUtmSource(utmRaw)
    if (assistant !== null) return aiSource(assistant)
  }

  if (referrerHost !== null) {
    if (isSearchHost(referrerHost)) {
      return { channel: 'search', assistant: null, site: referrerHost }
    }
    if (matchesAny(referrerHost, SOCIAL_HOSTS)) {
      return { channel: 'social', assistant: null, site: referrerHost }
    }
    return { channel: 'referral', assistant: null, site: referrerHost }
  }

  if (nonEmpty(input.gclid) !== null) {
    return { channel: 'search', assistant: null, site: null }
  }
  if (utmRaw !== null) {
    const value = normaliseHost(utmRaw)
    if (SEARCH_UTM_NAMES.some((name) => name === value) || isSearchHost(value)) {
      return { channel: 'search', assistant: null, site: utmRaw }
    }
    if (SOCIAL_UTM_NAMES.some((name) => name === value) || matchesAny(value, SOCIAL_HOSTS)) {
      return { channel: 'social', assistant: null, site: utmRaw }
    }
    return { channel: 'referral', assistant: null, site: utmRaw }
  }

  return { channel: 'direct', assistant: null, site: null }
}

/**
 * The assistant a GA4 `sessionSource` names, or null. GA4's `sessionSource` is the referrer host or
 * the `utm_source`, so this is the same match `classifyTrafficSource` makes for a `utm_source`
 * (bare names and hosts, with sub-hosts and `www.`), never GA4's own channel group.
 */
export function assistantForSessionSource(source: string): AssistantId | null {
  return classifyTrafficSource({ utmSource: source }).assistant
}

/**
 * The shortest alternation covering every assistant term, for a server-side case-insensitive
 * `PARTIAL_REGEXP` filter on GA4's `sessionSource`. Derived from the registry: every assistant's
 * `utmSources` and hosts, minus any term that contains another term (`chatgpt.com` contains
 * `chatgpt`), dots escaped, joined with `|`. A plain RE2-compatible alternation (no lookarounds,
 * no backreferences). It is a superset of what `assistantForSessionSource` accepts, so it can
 * over-match (`poetry.com` contains `poe`) and never under-match; the classifier is the arbiter.
 */
export const AI_SESSION_SOURCE_PATTERN: string = (() => {
  const terms: string[] = []
  for (const assistant of AI_ASSISTANTS) {
    for (const term of [...assistant.utmSources, ...assistant.hosts]) {
      if (!terms.includes(term)) terms.push(term)
    }
  }
  return terms
    .filter((term) => !terms.some((other) => other !== term && term.includes(other)))
    .map((term) => term.replace(/\./g, '\\.'))
    .join('|')
})()

/**
 * One sign-up's source, from a validated `Attribution` (never raw jsonb). Not captured when the
 * attribution has no landing, no referrer, no `utm_source` and no `gclid`: we never saw a first
 * landing (for example an EU visitor without consent who browsed first, or any pre-R6.10 sign-up
 * that only carried `ref`/`mode`). Direct therefore requires a recorded landing.
 */
export function classifySignupSource(attribution: Attribution | null): SignupSource {
  const seen =
    attribution !== null &&
    Boolean(
      attribution.landing_path ??
      attribution.referrer_host ??
      attribution.utm_source ??
      attribution.gclid
    )
  if (attribution === null || !seen) {
    return { source: 'not_captured', assistant: null, site: null }
  }
  const traffic = classifyTrafficSource({
    referrerHost: attribution.referrer_host,
    utmSource: attribution.utm_source,
    gclid: attribution.gclid,
  })
  return { source: traffic.channel, assistant: traffic.assistant, site: traffic.site }
}

/** The copy for every assistant and channel: what `/admin/signups` and slice 3 show. */
export const SIGNUP_SOURCE_LABELS: Record<AssistantId | Exclude<SignupSourceKey, 'ai'>, string> = {
  ...(Object.fromEntries(
    AI_ASSISTANTS.map((assistant) => [assistant.id, assistant.label])
  ) as Record<AssistantId, string>),
  search: 'Search',
  social: 'Social',
  referral: 'Referral',
  direct: 'Direct',
  not_captured: 'Not captured',
}
